import { PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react';

import { CompareItemsDialog } from '@/dialogs/compare-items/CompareItemsDialog';
import { ExportMediaAssetDialog } from '@/dialogs/export-media-asset/ExportMediaAssetDialog';
import { FocusAttributeProvider } from '@/dialogs/item-editor/Context/FocusAttributeProvider';
import { ItemEditorDialog } from '@/dialogs/item-editor/ItemEditorDialog';
import { MassUpdateDialog } from '@/dialogs/mass-update/MassUpdateDialog';
import { PriorItemDialog } from '@/dialogs/prior-item/PriorItemDialog';
import { PublishToRecipientsDialog } from '@/dialogs/publish-to-recipients/PublishToRecipientsDialog';
import {
    DialogHostApi,
    DialogHostContext,
    OpenItemEditorParams,
    OpenMassUpdateParams,
    OpenMediaAssetExportParams,
    OpenPriorItemParams,
    OpenPublishToRecipientsParams,
} from '@/hooks/useDialogHost';
import { getInjector, getRootScope, hasSettingFeature } from '@/services/angularjs-bridge';
import type { PersistedItem } from '@/types/DataModel';

type ActiveDialog =
    | { type: 'itemEditor'; mountId: number; props: OpenItemEditorParams }
    | { type: 'compare'; mountId: number; items: unknown[]; currentLayout: string }
    | { type: 'massUpdate'; mountId: number; props: OpenMassUpdateParams }
    | { type: 'priorItem'; mountId: number; props: OpenPriorItemParams }
    | { type: 'mediaAssetExport'; mountId: number; props: OpenMediaAssetExportParams }
    | { type: 'publishToRecipients'; mountId: number; props: OpenPublishToRecipientsParams };

const DialogHost = ({ children }: PropsWithChildren) => {
    const [active, setActive] = useState<ActiveDialog | null>(null);
    const activeRef = useRef<ActiveDialog | null>(null);
    const mountIdRef = useRef(0);

    activeRef.current = active;

    const nextMountId = useCallback(() => {
        mountIdRef.current += 1;
        return mountIdRef.current;
    }, []);

    const closeAll = useCallback(() => {
        setActive(null);
    }, []);

    const openItemEditor = useCallback((props: OpenItemEditorParams) => {
        setActive({ type: 'itemEditor', mountId: nextMountId(), props });
    }, [nextMountId]);

    const openCompare = useCallback((items: unknown[], options?: { currentLayout?: string }) => {
        setActive({
            type: 'compare',
            mountId: nextMountId(),
            items,
            currentLayout: options?.currentLayout ?? '',
        });
    }, [nextMountId]);

    const openMassUpdate = useCallback((props: OpenMassUpdateParams) => {
        setActive({ type: 'massUpdate', mountId: nextMountId(), props });
    }, [nextMountId]);

    const openPriorItem = useCallback((props: OpenPriorItemParams) => {
        setActive({ type: 'priorItem', mountId: nextMountId(), props });
    }, [nextMountId]);

    const openMediaAssetExport = useCallback((props: OpenMediaAssetExportParams) => {
        setActive({ type: 'mediaAssetExport', mountId: nextMountId(), props });
    }, [nextMountId]);

    const openPublishToRecipients = useCallback((props: OpenPublishToRecipientsParams) => {
        if (!hasSettingFeature('NEXT_PUBLICATIONS')) {
            const injector = getInjector();
            const $modal = injector.get('$modal');
            $modal.open({
                templateUrl: 'tpl/publish-to-recipients-modal.tpl.html',
                controller: 'PublishItemsController',
                backdrop: true,
                windowClass: 'publish-modal',
                resolve: {
                    itemSelection: () => props.itemSelection,
                    mode: () => props.mode,
                    publicationData: () => props.publicationData ?? {},
                },
            }).result.then(() => {
                props.onCompleted?.();
            }, () => undefined).finally(() => {
                props.onClosed?.();
            });
            return;
        }
        setActive({ type: 'publishToRecipients', mountId: nextMountId(), props });
    }, [nextMountId]);

    const api = useMemo<DialogHostApi>(() => ({
        openItemEditor,
        openCompare,
        openMassUpdate,
        openPriorItem,
        openMediaAssetExport,
        openPublishToRecipients,
        closeAll,
    }), [
        openItemEditor,
        openCompare,
        openMassUpdate,
        openPriorItem,
        openMediaAssetExport,
        openPublishToRecipients,
        closeAll,
    ]);

    const handleItemEditorNavigate = useCallback((item?: PersistedItem) => {
        const current = activeRef.current;
        const prev = current?.type === 'itemEditor' ? current.props : undefined;
        if (!prev) {
            return;
        }
        openItemEditor({
            ...prev,
            primaryKey: item?.primaryKey__ ?? prev.primaryKey,
            originalPrimaryKey: item?.primaryKey__ ?? prev.originalPrimaryKey,
            category: item?.category__ ?? prev.category,
        });
    }, [openItemEditor]);

    return (
        <DialogHostContext.Provider value={api}>
            {children}
            {active?.type === 'itemEditor' && (
                <FocusAttributeProvider>
                    <ItemEditorDialog
                        key={active.mountId}
                        category={active.props.category}
                        editorState={active.props.editorState}
                        forceCloseTask={active.props.forceCloseTask}
                        originalPrimaryKey={active.props.originalPrimaryKey}
                        primaryKey={active.props.primaryKey}
                        task={active.props.task}
                        items={active.props.items}
                        templateForItemCopy={active.props.templateForItemCopy}
                        templateForPreparedDefaultItem={active.props.templateForPreparedDefaultItem}
                        fromPreparedDefaultItem={active.props.fromPreparedDefaultItem}
                        onClose={(item) => {
                            active.props.onSaved?.(item);
                            closeAll();
                        }}
                        openItemEditor={handleItemEditorNavigate}
                    />
                </FocusAttributeProvider>
            )}
            {active?.type === 'compare' && (
                <CompareItemsDialog
                    key={active.mountId}
                    items={active.items}
                    rootScope={getRootScope(getInjector())}
                    currentLayout={active.currentLayout}
                    onClose={closeAll}
                />
            )}
            {active?.type === 'massUpdate' && (
                <FocusAttributeProvider>
                    <MassUpdateDialog
                        key={active.mountId}
                        selection={active.props.selection}
                        item={active.props.item}
                        category={active.props.category}
                        numberFound={active.props.numberFound}
                        maxNumberFound={active.props.maxNumberFound}
                        onClose={(_toWait, completed) => {
                            if (completed) {
                                active.props.onCompleted?.();
                            }
                            closeAll();
                        }}
                    />
                </FocusAttributeProvider>
            )}
            {active?.type === 'priorItem' && (
                <FocusAttributeProvider>
                    <PriorItemDialog
                        key={active.mountId}
                        dialogTitle={active.props.dialogTitle}
                        isCategorySpecific={active.props.isCategorySpecific}
                        preselectedCategory={active.props.preselectedCategory}
                        getTemplateAttributesMap={active.props.getTemplateAttributesMap}
                        onClose={(_toWait, values) => {
                            if (values) {
                                active.props.onSubmit?.(values);
                            }
                            closeAll();
                        }}
                    />
                </FocusAttributeProvider>
            )}
            {active?.type === 'mediaAssetExport' && (
                <ExportMediaAssetDialog
                    key={active.mountId}
                    items={active.props.items}
                    itemsSelection={active.props.itemsSelection}
                    onClose={closeAll}
                />
            )}
            {active?.type === 'publishToRecipients' && (
                <FocusAttributeProvider>
                    <PublishToRecipientsDialog
                        key={active.mountId}
                        itemSelection={active.props.itemSelection}
                        mode={active.props.mode}
                        publicationData={active.props.publicationData}
                        onClose={(_toWait, completed) => {
                            if (completed) {
                                active.props.onCompleted?.();
                            }
                            active.props.onClosed?.();
                            closeAll();
                        }}
                    />
                </FocusAttributeProvider>
            )}
        </DialogHostContext.Provider>
    );
};

DialogHost.displayName = 'DialogHost';

export { DialogHost };
